/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { forwardRef, type ForwardedRef } from "react";

import { cssFns, useCss } from "@superweb/css";

import { useUiColors } from "../../theme";
import { useTypo } from "../../typo";

import type { TextColumn } from "../types";
import { Cell } from "./cells";

export const TextCell = forwardRef(
  <D extends Object>(
    {
      column,
      row,
      rowIndex,
      onPress,
      isHovered = false,
      isStickyFirstColumn = false,
      stickyFirstColumnShift = 0,
      index,
      tabIndex,
      updateFocusPointer,
      variant = "fill",
      position,
      isTopPinned = false,
      isBottomPinned = false,
      isLastRow = false,
      hasSelection,
      selectionOffset,
    }: {
      index: number;
      tabIndex?: number;
      column: TextColumn<D>;
      row: D;
      rowIndex: number;
      onPress?: () => void;
      isHovered?: boolean;
      isTopPinned?: boolean;
      isBottomPinned?: boolean;
      isStickyFirstColumn?: boolean;
      stickyFirstColumnShift?: number;
      updateFocusPointer: () => void;
      variant?: "fill" | "ghost";
      isLastRow?: boolean;
      hasSelection: boolean;
      selectionOffset: number;
      position: {
        firstLeft?: boolean;
        firstRight?: boolean;
        lastLeft?: boolean;
        lastRight?: boolean;
      };
    },
    ref: ForwardedRef<HTMLTableCellElement>,
  ) => {
    const typo = useTypo();
    const uiColors = useUiColors();

    const css = useCss();

    const Icon = column.icon?.(row, rowIndex);
    const orientation = column.orientation ?? "vertical";

    return (
      <Cell
        ref={ref}
        index={index}
        tabIndex={tabIndex}
        onPress={onPress}
        isHovered={isHovered}
        isStickyFirstColumn={isStickyFirstColumn}
        stickyFirstColumnShift={stickyFirstColumnShift}
        updateFocusPointer={updateFocusPointer}
        variant={variant}
        align={"start"}
        position={position}
        isTopPinned={isTopPinned}
        isBottomPinned={isBottomPinned}
        isLastRow={isLastRow}
        hasSelection={hasSelection}
        selectionOffset={selectionOffset}
      >
        <div
          css={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {Icon && (
            <div
              css={{
                display: "flex",
                alignItems: "center",
                marginInlineEnd: "8px",
              }}
            >
              <Icon
                className={css({
                  width: "20px",
                  height: "20px",
                })}
              />
            </div>
          )}
          <div
            css={{
              display: "flex",
              ...(orientation === "vertical"
                ? {
                    flexDirection: "column",
                  }
                : { flexDirection: "row", alignItems: "center" }),
              ...cssFns.overflow("hidden"),
              textOverflow: "ellipsis",
            }}
          >
            <span
              css={{
                color: uiColors.text,
                ...typo({
                  level: "body2",
                  weight: "regular",
                  density: "tight",
                }),
                ...(orientation === "vertical"
                  ? {
                      ...cssFns.overflow("hidden"),
                      textOverflow: "ellipsis",
                    }
                  : { marginInlineEnd: "8px" }),
              }}
            >
              {column.value(row, rowIndex)}
            </span>
            {column.description && (
              <span
                css={{
                  color: uiColors.textMinor,
                  ...(orientation === "vertical"
                    ? typo({
                        level: "caption1",
                        weight: "regular",
                        density: "tight",
                      })
                    : typo({
                        level: "body2",
                        weight: "regular",
                        density: "tight",
                      })),
                  ...cssFns.overflow("hidden"),
                  textOverflow: "ellipsis",
                }}
              >
                {column.description(row, rowIndex)}
              </span>
            )}
          </div>
        </div>
      </Cell>
    );
  },
);
